import { KeyboardEvent, useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import TargetCursor from "../../components/TargetCursor";
import GracefulImage from "../../components/GracefulImage";
import { kgfThumbnail } from "../../constants/assets";
import {
  KillerSectionShell,
  KillerSectionTitle,
  KillerSoftSubText,
  KillerSubHeader,
  KillerTextColumn,
  KillerTwoColumnLayout,
} from "./killerSectionLayout";

const PhotoWall = styled.div`
  flex: 0 0 clamp(40rem, 42vw, 48rem);
  width: clamp(40rem, 42vw, 48rem);
  height: clamp(46rem, 50vw, 58rem);
  position: relative;
  margin-left: clamp(0rem, 2vw, 2rem);
  padding: 0;
  order: 1;

  @media only screen and (max-width: 600px) {
    width: 100vw;
    max-width: none;
    height: clamp(44rem, 132vw, 56rem);
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    margin-top: 1.2rem;
    margin-bottom: 2rem;
    padding-bottom: 2.4rem;
    order: 2;
    overflow: visible;
  }

  @media only screen and (min-width: 601px) and (max-width: 1200px) {
    flex-basis: clamp(36rem, 39vw, 44rem);
    width: clamp(36rem, 39vw, 44rem);
    height: clamp(41rem, 46vw, 51rem);
    margin-left: 0;
    order: 1;
  }
`;

const shotShake = keyframes`
  0% {
    transform: translate3d(0, 0, 0) rotate(0deg) scale(1);
  }
  10% {
    transform: translate3d(-2px, 1px, 0) rotate(-0.4deg) scale(1.015);
  }
  24% {
    transform: translate3d(18px, -8px, 0) rotate(3.8deg) scale(0.95);
  }
  42% {
    transform: translate3d(-9px, 4px, 0) rotate(-1.6deg) scale(1.01);
  }
  62% {
    transform: translate3d(4px, -2px, 0) rotate(0.7deg) scale(0.997);
  }
  100% {
    transform: translate3d(0, 0, 0) rotate(0deg) scale(1);
  }
`;

const PolaroidCard = styled.figure<{
  $transform?: string;
  $mobileTransform?: string;
  $zIndex?: number;
}>`
  width: clamp(15rem, 17vw, 18.6rem);
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: ${({ $transform }) => $transform || "none"};
  transform-origin: center center;
  cursor: pointer;
  z-index: ${({ $zIndex = 1 }) => $zIndex};

  &:hover,
  &:focus-visible {
    z-index: 12;
  }

  @media only screen and (max-width: 600px) {
    width: clamp(10.8rem, 34vw, 13.5rem);
    transform: ${({ $mobileTransform, $transform }) =>
      $mobileTransform || $transform || "none"};
  }

  @media only screen and (min-width: 601px) and (max-width: 1200px) {
    width: clamp(13.2rem, 15vw, 16rem);
  }
`;

const PolaroidShakeLayer = styled.div<{ $isShot?: boolean }>`
  width: 100%;
  transform-origin: center center;

  ${({ $isShot }) =>
    $isShot &&
    css`
      animation: ${shotShake} 340ms cubic-bezier(0.18, 0.82, 0.32, 1);
    `}
`;

const PolaroidShell = styled.div`
  position: relative;
  background: #ffffff;
  padding: 0.9rem 0.9rem 4.6rem;
  border: 1px solid rgba(17, 17, 17, 0.08);
  box-shadow: 0 24px 42px rgba(24, 24, 24, 0.14);
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  ${PolaroidCard}:hover &,
  ${PolaroidCard}:focus-visible & {
    transform: translateY(-2px) scale(1.08);
    box-shadow: 0 30px 52px rgba(24, 24, 24, 0.24);
  }

  @media only screen and (max-width: 600px) {
    padding: 0.75rem 0.75rem 3.8rem;

    ${PolaroidCard}:hover &,
    ${PolaroidCard}:focus-visible &,
    ${PolaroidCard}:active & {
      transform: translateY(-1px) scale(1.03);
      box-shadow: 0 24px 42px rgba(24, 24, 24, 0.2);
    }
  }
`;

const PolaroidImage = styled(GracefulImage)`
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`;

const PolaroidLabel = styled.figcaption`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 1.3rem;
  text-align: center;
  font-family: "JetBrains Mono", "Space Grotesk", sans-serif;
  font-size: 1.25rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(24, 24, 24, 0.68);

  @media only screen and (max-width: 600px) {
    font-size: 1.1rem;
    bottom: 1.15rem;
  }
`;

const kgfPhotos = [
  {
    src: kgfThumbnail(5669),
    label: "Killer GF #5669",
    transform:
      "translate(calc(-50% - 11rem), calc(-50% - 13rem)) rotate(-10deg)",
    mobileTransform:
      "translate(calc(-50% - 10.2rem), calc(-50% - 11.5rem)) rotate(-8deg)",
    zIndex: 2,
  },
  {
    src: kgfThumbnail(4),
    label: "Killer GF #4",
    transform:
      "translate(calc(-50% + 10.6rem), calc(-50% - 9.2rem)) rotate(11deg)",
    mobileTransform:
      "translate(calc(-50% + 10.2rem), calc(-50% - 10rem)) rotate(8deg)",
    zIndex: 2,
  },
  {
    src: kgfThumbnail(5),
    label: "Killer GF #5",
    transform: "translate(calc(-50% - 0.4rem), calc(-50% + 1.8rem)) rotate(-3deg)",
    mobileTransform:
      "translate(calc(-50% + 0rem), calc(-50% + 0.4rem)) rotate(-2deg)",
    zIndex: 4,
  },
  {
    src: kgfThumbnail(200),
    label: "Killer GF #200",
    transform:
      "translate(calc(-50% - 10.8rem), calc(-50% + 15rem)) rotate(8deg)",
    mobileTransform:
      "translate(calc(-50% - 10rem), calc(-50% + 11.8rem)) rotate(6deg)",
    zIndex: 1,
  },
  {
    src: kgfThumbnail(3),
    label: "Killer GF #3",
    transform:
      "translate(calc(-50% + 10.8rem), calc(-50% + 11.8rem)) rotate(-12deg)",
    mobileTransform:
      "translate(calc(-50% + 10rem), calc(-50% + 10.8rem)) rotate(-8deg)",
    zIndex: 1,
  },
];



const KillerGF = () => {
  const [shotLabel, setShotLabel] = useState<string | null>(null);
  const shotTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (shotTimeoutRef.current) {
        window.clearTimeout(shotTimeoutRef.current);
      }
    };
  }, []);

  const triggerShot = (label: string) => {
    if (shotTimeoutRef.current) {
      window.clearTimeout(shotTimeoutRef.current);
    }

    setShotLabel(label);
    shotTimeoutRef.current = window.setTimeout(() => {
      setShotLabel((current) => (current === label ? null : current));
      shotTimeoutRef.current = null;
    }, 420);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    label: string
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      triggerShot(label);
    }
  };

  return (
    <KillerSectionShell id="artist-scroll" $background="#ffffff">
      <TargetCursor
        targetSelector=".kgf-polaroid"
        geometrySelector=".kgf-polaroid-image"
        scopeSelector="#artist-scroll"
        pauseSelector=".kgf-cursor-pause"
        spinDuration={2.2}
        hideDefaultCursor={false}
        hoverDuration={0.22}
        parallaxOn={true}
        parallaxStrength={0.35}
        idleOpacity={0.92}
        idleScale={1}
        activeScale={1.35}
        cornerSize={10}
        borderWidth={2}
        dotSize={14}
        targetPadding={2}
        freezeSelector=".kgf-polaroid-shot"
        color="#ff0066"
      />
      <KillerTwoColumnLayout $mobileVariant="kgf">
      <KillerTextColumn $variant="kgf">
        <KillerSectionTitle>Where It All Started</KillerSectionTitle>
        <KillerSubHeader>
          <a className="kgf-cursor-pause" href="https://opensea.io/collection/killergf" target="_blank" rel="noopener noreferrer">Killer GF</a> is a bold onchain art collection created by Kusari in January 2022, featuring 7,777 badass assassins who double as captivating girlfriends.
        </KillerSubHeader>
        <KillerSoftSubText $tone="dark">
          Each piece is uniquely generated from over 240 hand-drawn traits including weapons, hairstyles, clothing and expressions. There are 7 hand-crafted 1/1 pieces in the collection.
        </KillerSoftSubText>
      </KillerTextColumn>
      <PhotoWall>
        {kgfPhotos.map(({ src, label, transform, mobileTransform, zIndex }, index) => (
          <PolaroidCard
            key={label}
            $transform={transform}
            $mobileTransform={mobileTransform}
            $zIndex={zIndex}
            className={`kgf-polaroid${shotLabel === label ? " kgf-polaroid-shot" : ""}`}
            onClick={() => triggerShot(label)}
            onKeyDown={(event) => handleKeyDown(event, label)}
            role="button"
            tabIndex={0}
            aria-label={`Trigger impact effect for ${label}`}
          >
            <PolaroidShakeLayer $isShot={shotLabel === label}>
              <PolaroidShell className="kgf-polaroid-shell">
                <PolaroidImage
                  className="kgf-polaroid-image"
                  src={src}
                  alt={label}
                  aspectRatio="1 / 1"
                  placeholderTone="light"
                  staggerIndex={index}
                  staggerMs={40}
                />
                <PolaroidLabel>{label}</PolaroidLabel>
              </PolaroidShell>
            </PolaroidShakeLayer>
          </PolaroidCard>
        ))}
      </PhotoWall>
      </KillerTwoColumnLayout>
    </KillerSectionShell>
  );
};

export default KillerGF;
